import { PropsWithChildren, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { TOKEN_LOGOUT } from '@/utils';

import { LoaderScreen } from '@/elements/PageLoader';

import { SearchParamsEnum } from '@/enums/searchParams.enum';
import { StorageKeysEnum } from '@/enums/storageKeys.enum';

import useGoogleAnalytics from '@/hooks/GoogleAnalytics/useGoogleAnalytics';
import { useJwt } from '@/hooks/useJwt';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useLogout } from '@/hooks/useLogout';

import {
  useLoginMutation,
  useRefreshTokenMutation,
} from '@/services/api.service';

import { useAuthStore } from '@/store/hooks/useAuthStore';

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const { isJwtLoading, setIsJwtLoading } = useAuthStore();
  const { jwt, setJwt, setRefreshToken } = useJwt();
  const { setItem: setLang } = useLocalStorage<string>(
    StorageKeysEnum.i18nextLng,
  );
  const { setItem: setFavouriteTeamId } = useLocalStorage<number>(
    StorageKeysEnum.favourite_team_league_id,
  );

  const { setItem: setClientToken } = useLocalStorage<string>(
    StorageKeysEnum.clientToken,
  );
  const { value: refreshToken, removeItem: removeRefreshToken } =
    useLocalStorage<string>(StorageKeysEnum.refreshToken);
  const { clearAuthData } = useLogout();

  useGoogleAnalytics();

  useEffect(() => {
    // Save client token to localStorage to use it in redirect to gamezone link
    const clientToken = searchParams.get(SearchParamsEnum.clientToken);
    if (clientToken) {
      setClientToken(clientToken);
      searchParams.delete(SearchParamsEnum.clientToken);
      setSearchParams(searchParams);
    }
  });

  const token = useMemo(
    () => searchParams.get(SearchParamsEnum.token) as string,
    [searchParams],
  );
  const lang = useMemo(
    () => searchParams.get(SearchParamsEnum.lang) as string,
    [searchParams],
  );

  const [triggerLogin, { isLoading, isSuccess }] = useLoginMutation();
  const [triggerRefreshToken, { isLoading: isRefreshTokenLoading }] =
    useRefreshTokenMutation();

  useEffect(() => {
    if (isSuccess || jwt) {
      setIsJwtLoading(false);
    }
  }, [isSuccess, jwt, setIsJwtLoading]);

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang, () => setLang(lang));
      searchParams.delete(SearchParamsEnum.lang);
      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, lang]);

  const makeLogin = async () => {
    try {
      const res = await triggerLogin({ token }).unwrap();
      setJwt(res.token);
      setRefreshToken(res.refresh_token);
      setFavouriteTeamId(res.favourite_team_league_id);

      // Remove token from search params
      searchParams.delete(SearchParamsEnum.token);
      setSearchParams(searchParams);
    } catch (e: any) {
      if (e.status === 400) {
        clearAuthData();

        if (refreshToken) {
          try {
            const response = await triggerRefreshToken({
              refreshToken,
            }).unwrap();
            setJwt(response.token);
            setRefreshToken(response.refresh_token);
          } catch (error) {
            removeRefreshToken();
          }
        }
      }
    } finally {
      setIsJwtLoading(false);
    }
  };

  useEffect(() => {
    // Do not run this effect if there is no token
    if (!token) return;

    if (token === TOKEN_LOGOUT) {
      // Remove token from search params
      searchParams.delete(SearchParamsEnum.token);
      setSearchParams(searchParams);

      clearAuthData();
    }

    makeLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <>
      {children}
      {(isJwtLoading || isLoading || isRefreshTokenLoading) && <LoaderScreen />}
    </>
  );
};
